// src/components/SecurityModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import secMasterFields from '../data/secMasterFields.json';
import {
  getNaicDesignationFromRating, getCollateralTypeFromAssetType, isCreOnlyField, isSyntheticCusipPpn,
} from '../utils/secMasterUtils';

const SecurityModal = ({ open, onClose, onSave, securityType, userId, secMasterData }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [mustReopen, setMustReopen] = useState(false);
  // Percentage fields the user has typed in since the modal opened.
  const [typedPercentFields, setTypedPercentFields] = useState(() => new Set());
  const saving = useRef(false);
  const isEditMode = !!secMasterData;

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      let initialData = {};

      if (isEditMode && secMasterData) {
        // Edit mode: use existing data
        initialData = { ...secMasterData };

        // Convert date strings to dayjs objects for date fields
        const dateFields = ['TRADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE', 'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE'];
        dateFields.forEach(field => {
          const fieldValue = initialData[field];
          // Always initialize date fields, defaulting to null if missing or invalid
          if (fieldValue != null && fieldValue !== '' && fieldValue !== undefined) {
            try {
              // If it's already a dayjs object, check if valid
              if (fieldValue && typeof fieldValue.isValid === 'function') {
                initialData[field] = fieldValue.isValid() ? fieldValue : null;
              } else {
                // Try to parse as dayjs
                const dateValue = dayjs(fieldValue);
                if (dateValue && typeof dateValue.isValid === 'function' && dateValue.isValid()) {
                  initialData[field] = dateValue;
                } else {
                  initialData[field] = null;
                }
              }
            } catch (error) {
              // If parsing fails, set to null
              console.warn(`Error parsing date field ${field}:`, error);
              initialData[field] = null;
            }
          } else {
            // Ensure field is always set to null (not undefined or empty string)
            initialData[field] = null;
          }
        });

        // A NULL (or '') boolean shows unchecked but stays blank until the user toggles
        // it, so an untouched value is not saved as false.
        const booleanFields = ['FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE'];
        booleanFields.forEach(field => {
          if (initialData[field] === undefined) {
            initialData[field] = null;
          }
        });

        // If RATING exists, automatically set NAIC_DESIGNATION
        if (initialData.RATING) {
          const naicDesignation = getNaicDesignationFromRating(initialData.RATING);
          if (naicDesignation) {
            initialData.NAIC_DESIGNATION = naicDesignation;
          }
        }

        // If ASSET_TYPE exists, automatically set COLLATERAL_TYPE
        if (initialData.ASSET_TYPE) {
          const collateralType = getCollateralTypeFromAssetType(initialData.ASSET_TYPE);
          if (collateralType) {
            initialData.COLLATERAL_TYPE = collateralType;
          }
        }
      } else {
        // Add mode: use defaults
        initialData = {
          ASSET_TYPE: securityType || '',
          SECURITY_OWNER: '',
          SECURITY_OWNER_TEAM: '',
          SECURITY_DESCRIPTION: '',
          CUSIP_PPN: '',
          TRADE_DATE: dayjs(),
          ISSUER: '',
          TICKER: '',
          SECURITY_TYPE: '',
          SETTLE_DATE: dayjs(),
          SETTLEMENT_TYPE: '',
          TOTAL_COMMITMENT: 0.0,
          STAT_CURRENT_UNITS: 0.0,
          STAT_BOOK_VALUE: 0.0,
          STAT_BOOK_YIELD: '',
          TRADE_PRICE: 0.0,
          STAT_CLASSIFICATION: '',
          NAIC_GENERAL_CATEGORY: '',
          AFFILIATED_UNAFFILIATED: '',
          COLLATERAL_TYPE: securityType ? (getCollateralTypeFromAssetType(securityType) || '') : '',
          BROKER_DEALER: '',
          CLOSING_CONTACT: '',
          CONNECTED_ENTITY: '',
          COUNTRY_OF_ISSUE: 'United States',
          CURRENCY: 'USD',
          PIK_INTEREST: '',
          COUPON_FREQUENCY: '',
          COUPON_TYPE: '',
          FIRST_COUPON_DATE: dayjs(),
          FLOATER: false,
          BASE_RATE: '',
          FLOATER_SPREAD: '',
          ALL_IN_RATE: '',
          DAY_COUNT: '',
          MATURITY_DATE: dayjs(),
          BALLOON_PAYMENT: false,
          ILLIQUID: false,
          PRIVATE_RATING: false,
          RATING_ESTIMATE: false,
          RATING: '',
          NAIC_DESIGNATION: '',
          RATING_AGENCY: '',
          RATING_DATE: dayjs(),
          REFERENCE_INDEX: '',
          COMMENTS: '',
          STATUS: 'Active',
          AMORTIZATION_TYPE: '',
          ACCRUED_INTEREST_START_DATE: dayjs(),
          CW_ACCOUNT_NAME: '',
          CW_ACCOUNT_NUMBER: '',
          CUSTODIAN_NAME: '',
          CUSTODIAN_ACCOUNT_NUMBER: '',
          LAST_EXPORT_USER: '',
          LAST_EXPORT_DATE: null
        };
      }

      setFormData(initialData);
      setErrors({});
      setMustReopen(false);
      setTypedPercentFields(new Set());
    }
  }, [open, securityType, userId, secMasterData, isEditMode]);

  const handleInputChange = (field, value) => {
    if (saving.current) return;
    // Percentage fields keep the text exactly as typed: rewriting it on every
    // keystroke ("1" -> "100%") corrupts the next character. Validation
    // accepts "5.5%" or "0.055", and handleSubmit converts both to a decimal.
    const percentageFields = ['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'];
    const processedValue = value;
    if (percentageFields.includes(field)) {
      setTypedPercentFields(prev => (prev.has(field) ? prev : new Set(prev).add(field)));
    }

    const updatedFormData = {
      ...formData,
      [field]: processedValue
    };

    // If RATING field is updated, automatically update NAIC_DESIGNATION
    if (field === 'RATING') {
      const naicDesignation = getNaicDesignationFromRating(processedValue);
      updatedFormData.NAIC_DESIGNATION = naicDesignation;
    }

    // If ASSET_TYPE field is updated, automatically update COLLATERAL_TYPE
    if (field === 'ASSET_TYPE') {
      const collateralType = getCollateralTypeFromAssetType(processedValue);
      updatedFormData.COLLATERAL_TYPE = collateralType;
    }

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateField = (field, value) => {
    const fieldConfig = secMasterFields[field];
    if (!fieldConfig) return '';

    // Mandatory fields validation (CUSIP_PPN only required in add mode)
    const mandatoryFields = [
      'SECURITY_OWNER', 'SECURITY_DESCRIPTION',
      'ISSUER', 'SECURITY_TYPE', 'SETTLEMENT_TYPE', 'STAT_BOOK_YIELD', 'BROKER_DEALER',
      'CLOSING_CONTACT', 'PIK_INTEREST', 'COUPON_FREQUENCY', 'COUPON_TYPE', 'ALL_IN_RATE',
      'DAY_COUNT', 'RATING'
    ];

    // CUSIP_PPN is required only in add mode
    if (field === 'CUSIP_PPN' && !isEditMode) {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return 'This field is required';
      }
      // new_row_* is the grid's placeholder id for an unsaved row, never a CUSIP/PPN.
      if (isSyntheticCusipPpn(String(value).trim())) {
        return 'Enter the real CUSIP/PPN';
      }
    }

    if (mandatoryFields.includes(field)) {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return 'This field is required';
      }
    }

    // Float validation
    const floatFields = ['TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'TRADE_PRICE'];
    if (floatFields.includes(field)) {
      if (value !== null && value !== undefined && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
          return 'Value must be a number >= 0';
        }
      }
    }

    // Percentage validation - accept both percentage format (5.5%) and decimal format (0.055)
    const percentageFields = ['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'];
    if (percentageFields.includes(field) && value) {
      const valueStr = value.toString().trim();
      // Check if it's in percentage format (e.g., "5.5%")
      // Number(), not parseFloat(): the whole text must be numeric, so
      // '0.5%foo' or '5x%' is rejected instead of silently saving 0.5 / 5%.
      if (valueStr.endsWith('%')) {
        const numText = valueStr.slice(0, -1).trim();
        const numValue = Number(numText);
        if (!numText || !Number.isFinite(numValue) || numValue < 0 || numValue > 100) {
          return 'Percentage must be between 0% and 100%';
        }
      } else {
        // Check if it's in decimal format (e.g., "0.055")
        const numValue = Number(valueStr);
        if (!valueStr || !Number.isFinite(numValue) || numValue < 0 || numValue > 1) {
          return 'Decimal value must be between 0 and 1';
        }
      }
    }

    // Security Owner validation (should not end with '...')
    if (field === 'SECURITY_OWNER' && value && value.endsWith('...')) {
      return 'Value must be present';
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (saving.current || mustReopen || !validateForm()) return;
    saving.current = true;
    setIsSaving(true);
    try {
      await submitForm();
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  const submitForm = async () => {
    // Convert dayjs objects back to strings for date fields
    const dataToSave = { ...formData };
    const dateFields = ['TRADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE', 'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE'];

    dateFields.forEach(field => {
      if (dataToSave[field]) {
        if (dayjs.isDayjs(dataToSave[field]) && dataToSave[field].isValid()) {
          dataToSave[field] = dataToSave[field].format('YYYY-MM-DD');
        } else if (!dayjs.isDayjs(dataToSave[field])) {
          // If it's already a string, keep it as is
          // Otherwise set to null
          if (typeof dataToSave[field] !== 'string') {
            dataToSave[field] = null;
          }
        } else {
          // Invalid dayjs object
          dataToSave[field] = null;
        }
      } else {
        // Null or undefined
        dataToSave[field] = null;
      }
    });

    // Convert boolean fields
    const booleanFields = ['FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE'];
    booleanFields.forEach(field => {
      if (dataToSave[field] === undefined) {
        dataToSave[field] = false;
      }
    });

    // Convert percentage fields to decimal format (ensure they're stored as decimals)
    const percentageFields = ['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'];
    percentageFields.forEach(field => {
      if (dataToSave[field] !== null && dataToSave[field] !== undefined && dataToSave[field] !== '') {
        const valueStr = dataToSave[field].toString().trim();
        if (valueStr.endsWith('%')) {
          // Convert percentage to decimal
          const numValue = Number(valueStr.slice(0, -1).trim());
          if (Number.isFinite(numValue)) {
            dataToSave[field] = numValue / 100;
          }
        } else {
          // Already in decimal format, ensure it's a number
          const numValue = Number(valueStr);
          if (Number.isFinite(numValue)) {
            dataToSave[field] = numValue;
          }
        }
      }
    });

    // Generate a temporary CUSIP_PPN if not provided
    if (!dataToSave.CUSIP_PPN || dataToSave.CUSIP_PPN.trim() === '') {
      dataToSave.CUSIP_PPN = `new_row_${Date.now()}`;
    }

    // Filter to only include fields that are present in the modal (formData)
    // This ensures fields not in the modal are not overwritten with null
    const filteredData = {};
    Object.keys(dataToSave).forEach(field => {
      // Only include fields that are actually in formData (present in modal)
      // This preserves fields not in the modal by not including them in the save
      // CRE-only loan fields are display-only: SEC_MASTER cannot store them.
      if (formData.hasOwnProperty(field) && !isCreOnlyField(field)) {
        filteredData[field] = dataToSave[field];
      }
    });

    // MODIFIED_BY / MODIFIED_DATE are stamped by the writer; the grid sends the
    // loaded MODIFIED_DATE, never one set here.
    // Never re-submit a stale full-row draft after a 409: the parent reloads
    // the grid, but this form still contains fields from before that reload.
    try {
      const saved = await onSave(filteredData);
      if (saved === true) onClose();
      else if (saved === 'conflict') setMustReopen(true);
    } catch (error) {
      // An unexpected failure is not a version conflict; keep the draft
      // editable so the caller can retry after the error is shown.
      throw error;
    }
  };

  const renderField = (fieldKey, fieldConfig) => {
    // For date fields, default to null instead of empty string
    const isDateField = ['TR==ADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE', 'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE'].includes(fieldKey);
    const value = formData[fieldKey] !== undefined ? formData[fieldKey] : (isDateField ? null : '');
    const error = errors[fieldKey] || '';

    // Skip system fields that shouldn't be edited (except NAIC_DESIGNATION which we'll show as read-only)
    if (['MODIFIED_BY', 'MODIFIED_DATE', 'LAST_EXPORT_USER', 'LAST_EXPORT_DATE'].includes(fieldKey)) {
      return null;
    }

    // CRE-only loan fields come from the CRE app; SEC_MASTER cannot store them, so
    // they are shown read-only (CRE rows never open this form).
    if (isCreOnlyField(fieldKey)) {
      const creValue = value ?? '';
      return (
        <TextField
          key={fieldKey}
          label={fieldConfig.name}
          value={creValue}
          disabled
          fullWidth
          size="small"
          helperText="Maintained in the CRE app"
          InputLabelProps={{
            shrink: creValue !== '',
            sx: {
              whiteSpace: 'normal',
              lineHeight: 1.2
            }
          }}
        />
      );
    }

    // NAIC_DESIGNATION is read-only and automatically updated from RATING
    if (fieldKey === 'NAIC_DESIGNATION') {
      const naicValue = formData[fieldKey] || '';
      return (
        <TextField
          key={fieldKey}
          label={fieldConfig.name || 'NAIC Designation'}
          value={naicValue}
          disabled
          fullWidth
          size="medium"
          InputLabelProps={{
            shrink: !!naicValue
          }}
        />
      );
    }

    const commonProps = {
      fullWidth: true,
      value: value,
      error: !!error,
      helperText: error,
      size: 'small'
    };

    // Boolean fields (Checkboxes)
    if (['FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE'].includes(fieldKey)) {
      return (
        <FormControlLabel
          key={fieldKey}
          control={
            <Checkbox
              checked={value || false}
              onChange={(e) => handleInputChange(fieldKey, e.target.checked)}
            />
          }
          label={fieldConfig.name}
        />
      );
    }

    // Date fields
    if (['TRADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE', 'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE'].includes(fieldKey)) {
      // Safely convert value to dayjs, handling null, undefined, and invalid dates
      let dateValue = null;

      // Check if value exists and is not empty
      if (value != null && value !== '' && value !== undefined) {
        try {
          // If it's already a dayjs object with isValid method
          if (value && typeof value.isValid === 'function') {
            dateValue = value.isValid() ? value : null;
          } else {
            // Parse as dayjs - dayjs() can handle dayjs objects, strings, Date objects, etc.
            const parsedDate = dayjs(value);
            // Ensure parsedDate is a dayjs object and is valid
            if (parsedDate && typeof parsedDate.isValid === 'function') {
              dateValue = parsedDate.isValid() ? parsedDate : null;
            } else {
              dateValue = null;
            }
          }
        } catch (error) {
          // If anything fails, set to null
          console.warn(`Error parsing date for ${fieldKey}:`, error);
          dateValue = null;
        }
      }

      // Final safety check: ensure dateValue is either null or a valid dayjs object
      if (dateValue != null && (typeof dateValue.isValid !== 'function' || !dateValue.isValid())) {
        dateValue = null;
      }

      return (
        <DatePicker
          key={fieldKey}
          label={fieldConfig.name}
          value={dateValue}
          onChange={(newValue) => handleInputChange(fieldKey, newValue)}
          format="YYYY-MM-DD"
          slotProps={{
            textField: {
              ...commonProps,
              fullWidth: true,
              size: 'small',
              InputLabelProps: {
                shrink: !!dateValue,
                sx: {
                  whiteSpace: 'normal',
                  lineHeight: 1.2
                }
              }
            }
          }}
        />
      );
    }

    // Dropdown fields - use choices from secMasterFields if available
    if (fieldConfig.choices && Array.isArray(fieldConfig.choices)) {
      const isRequired = [
        'SECURITY_TYPE', 'SETTLEMENT_TYPE', 'PIK_INTEREST',
        'COUPON_FREQUENCY', 'COUPON_TYPE', 'DAY_COUNT', 'RATING'
      ].includes(fieldKey);

      return (
        <FormControl fullWidth key={fieldKey} size="small" error={!!error} required={isRequired}>
          <InputLabel
            sx={{
              whiteSpace: 'normal',
              lineHeight: 1.2
            }}
          >
            {fieldConfig.name}
          </InputLabel>
          <Select
            value={value}
            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
            label={fieldConfig.name}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
            sx={{
              minWidth: '200px',
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {!isRequired && (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {fieldConfig.choices.map((choice) => (
              <MenuItem
                key={choice}
                value={choice}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  minHeight: 'auto',
                  padding: '8px 16px'
                }}
              >
                {choice}
              </MenuItem>
            ))}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }

    // Number fields
    if (['TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'TRADE_PRICE'].includes(fieldKey)) {
      return (
        <TextField
          key={fieldKey}
          {...commonProps}
          label={fieldConfig.name}
          type="number"
          onChange={(e) => handleInputChange(fieldKey, parseFloat(e.target.value) || 0)}
          inputProps={{
            min: 0,
            step: 0.01
          }}
          InputLabelProps={{
            sx: {
              whiteSpace: 'normal',
              lineHeight: 1.2
            }
          }}
        />
      );
    }

    // Percentage fields (STAT_BOOK_YIELD, BASE_RATE, ALL_IN_RATE, FLOATER_SPREAD)
    if (['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'].includes(fieldKey)) {
      const isRequired = ['STAT_BOOK_YIELD', 'ALL_IN_RATE'].includes(fieldKey);

      // A loaded decimal is shown as a percent until the user types in the field;
      // from then on the field shows the user's own text.
      let displayValue = value ?? '';
      if (typedPercentFields.has(fieldKey)) {
        displayValue = value ?? '';
      } else if (value && typeof value === 'string') {
        const valueStr = value.trim();
        // If it's already a percentage string, use it as is
        if (valueStr.endsWith('%')) {
          displayValue = valueStr;
        } else {
          // Check if it's a decimal value (0-1 range)
          const numValue = parseFloat(valueStr);
          if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
            // It's a decimal, convert to percentage for display
            displayValue = (numValue * 100).toString() + '%';
          } else {
            // Not a decimal, use as is (might be invalid or already percentage without %)
            displayValue = valueStr;
          }
        }
      } else if (typeof value === 'number' && value >= 0 && value <= 1) {
        // Numeric decimal value, convert to percentage
        displayValue = (value * 100).toString() + '%';
      }

      return (
        <TextField
          key={fieldKey}
          {...commonProps}
          label={fieldConfig.name}
          required={isRequired}
          value={displayValue}
          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
          placeholder="e.g., 5.5% or 0.055"
          InputLabelProps={{
            shrink: !!value,
            sx: {
              whiteSpace: 'normal',
              lineHeight: 1.2
            }
          }}
        />
      );
    }

    // Text fields
    const isRequired = [
      'SECURITY_OWNER', 'SECURITY_DESCRIPTION', 'CUSIP_PPN', 'ISSUER',
      'BROKER_DEALER', 'CLOSING_CONTACT'
    ].includes(fieldKey);

    // CUSIP_PPN should be read-only in edit mode
    const isReadOnly = isEditMode && fieldKey === 'CUSIP_PPN';

    return (
      <TextField
        key={fieldKey}
        {...commonProps}
        label={fieldConfig.name}
        required={isRequired && !isReadOnly}
        disabled={isReadOnly}
        shrink={!!value}
        onChange={(e) => handleInputChange(fieldKey, e.target.value)}
        multiline={fieldKey === 'COMMENTS'}
        rows={fieldKey === 'COMMENTS' ? 3 : 1}
        InputLabelProps={{
          sx: {
            whiteSpace: 'normal',
            lineHeight: 1.2,
            fontSize: '0.875rem'
          }
        }}
        sx={{
          '& .MuiInputBase-input': {
            whiteSpace: fieldKey === 'COMMENTS' ? 'normal' : 'nowrap',
            wordBreak: fieldKey === 'COMMENTS' ? 'break-word' : 'normal'
          }
        }}
      />
    );
  };

  // Define field order based on Python code
  const fieldOrder = [
    'SECURITY_OWNER', 'SECURITY_OWNER_TEAM', 'SECURITY_DESCRIPTION', 'CUSIP_PPN',
    'TRADE_DATE', 'ISSUER', 'TICKER', 'SECURITY_TYPE', 'SETTLE_DATE', 'SETTLEMENT_TYPE',
    'TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'STAT_BOOK_YIELD',
    'TRADE_PRICE', 'STAT_CLASSIFICATION', 'NAIC_GENERAL_CATEGORY', 'AFFILIATED_UNAFFILIATED',
    'ASSET_TYPE', 'COLLATERAL_TYPE', 'BROKER_DEALER', 'CLOSING_CONTACT', 'CONNECTED_ENTITY',
    'COUNTRY_OF_ISSUE', 'CURRENCY', 'PIK_INTEREST', 'COUPON_FREQUENCY', 'COUPON_TYPE',
    'FIRST_COUPON_DATE', 'FLOATER', 'BASE_RATE', 'FLOATER_SPREAD', 'ALL_IN_RATE',
    'DAY_COUNT', 'MATURITY_DATE', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING',
    'RATING_ESTIMATE', 'RATING', 'NAIC_DESIGNATION', 'RATING_AGENCY', 'RATING_DATE',
    'REFERENCE_INDEX', 'COMMENTS', 'STATUS', 'AMORTIZATION_TYPE', 'ACCRUED_INTEREST_START_DATE',
    'CW_ACCOUNT_NAME', 'CW_ACCOUNT_NUMBER', 'CUSTODIAN_NAME', 'CUSTODIAN_ACCOUNT_NUMBER',
    'BORROWER', 'SERVICE_FEE', 'RECOURSE_TYPE', 'ORIGINAL_LOAN_TERM', 'INTEREST_ONLY_TERM',
    'LAST_EXPORT_USER', 'LAST_EXPORT_DATE'
  ];

  const getFieldWidth = (fieldKey) => {
    // Full width fields (12 columns = 100% width) - for all text fields, dropdowns, and most fields
    // Only exclude very short fields
    if (['TICKER', 'CURRENCY', 'FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE', 'RATING', 'NAIC_DESIGNATION'].includes(fieldKey)) {
      // Short fields can be 6 columns (50% width) - RATING and NAIC_DESIGNATION side by side
      return 6;
    }

    // All other fields get full width to ensure labels are fully visible
    return 12;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={isSaving ? undefined : onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {isEditMode
                ? `Edit Sec Master Entry - ${secMasterData?.CUSIP_PPN || 'Entry'}`
                : `Add New Security - ${securityType || 'Unknown Type'}`
              }
            </Typography>
            <IconButton onClick={onClose} disabled={isSaving} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: '1 1 auto',
            minHeight: 0,
            padding: 3
          }}
        >
          <Box sx={{ mt: 2, pb: 2 }}>
            {mustReopen && (
              <Typography color="warning.main" role="alert" sx={{ mb: 2 }}>
                The save was not confirmed. Close this form and reopen the refreshed row before trying again; your draft remains visible here for reference.
              </Typography>
            )}
            <fieldset disabled={isSaving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
            <Grid container spacing={3}>
              {fieldOrder
                .filter(fieldKey => secMasterFields[fieldKey])
                .map(fieldKey => {
                  const fieldConfig = secMasterFields[fieldKey];
                  const fieldComponent = renderField(fieldKey, fieldConfig);
                  if (!fieldComponent) return null;

                  return (
                    <Grid size={{ xs: 12, sm: getFieldWidth(fieldKey), md: getFieldWidth(fieldKey) }} key={fieldKey}>
                      {fieldComponent}
                    </Grid>
                  );
                })}
            </Grid>
            </fieldset>
          </Box>
        </DialogContent>
        <Box sx={{ px: 3, pb: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Please send the below documents to Middle Office before submitting.
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2">1. Credit Agreement</Typography>
            <Typography variant="body2">2. Settlement Instructions</Typography>
            <Typography variant="body2">3. Private Rating Letter (if applicable)</Typography>
            <Typography variant="body2">4. Servicing Contact at Agent - Remittances</Typography>
          </Box>
        </Box>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={onClose} color="secondary" disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={isSaving || mustReopen}>
            {isEditMode ? 'Save Changes' : 'Create Security'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SecurityModal;

